import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  btnPrimaryClass,
  btnSecondaryClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  formGridClass,
  formFullWidthClass,
  alertInfoClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  cardTitleClass,
} from "../dashboardStyles";
import {
  CreateBookingAction,
  GetAvailableTimeSlotsAuthAction,
} from "../../../store/action";
import { formatSlotLabel, fromISODate } from "../../../utils/dates";
import { serviceMinutes } from "../../../utils/serviceDuration";
import { notify, notifySaveError } from "../../../utils/notify";

/**
 * The short way to put a booking in the calendar.
 *
 * Opened by selecting a free stretch of the calendar (or the "New booking"
 * button), so the date, the time and usually the specialist are already known
 * and the first thing under the cursor is the customer's name. Email and phone
 * are optional here — staff booking a walk-in or a phone call often have a name
 * and nothing more, and the API accepts that from the dashboard (never from
 * the public page).
 *
 * The time is always one of the slots `IAvailabilityServices` offers, fetched
 * for the chosen specialist and service. The calendar's own grid is only a
 * hint: a click at 10:15 is honoured when 10:15 is on offer and otherwise the
 * form says so and asks for another time, rather than posting a slot the API
 * would refuse.
 */

// Per-browser convenience: staff tend to book the same service over and over,
// so a click on the calendar starts from the last one they used.
const LAST_SERVICE_KEY = "quickBooking.lastServiceId";

const readLastServiceId = () => {
  try {
    return window.localStorage.getItem(LAST_SERVICE_KEY) || "";
  } catch {
    return "";
  }
};

const rememberServiceId = (id) => {
  try {
    window.localStorage.setItem(LAST_SERVICE_KEY, String(id));
  } catch {
    // Blocked storage only costs the preselection next time.
  }
};

// Re-exported: callers and tests have long imported it from here.
export { serviceMinutes };

/**
 * The services a resource may be booked for. An empty `resourceIds` means
 * anyone, which is every service whose category has no team.
 */
export const servicesForResource = (services, resourceId) => {
  if (!resourceId) return services;
  const id = Number(resourceId);
  return services.filter(
    (s) => !s.resourceIds || s.resourceIds.length === 0 || s.resourceIds.includes(id),
  );
};

/**
 * Which service to start from.
 *
 * A dragged stretch says how long the appointment is, so a service of exactly
 * that length wins — drag an hour, get the one-hour service, as in any
 * calendar. Failing that, the last service used here, then the only one there
 * is. Otherwise nothing, and the form asks.
 */
export const pickService = (services, durationHint, lastServiceId) => {
  if (durationHint) {
    const exact = services.find((s) => serviceMinutes(s) === durationHint);
    if (exact) return String(exact.id);
  }

  if (lastServiceId && services.some((s) => String(s.id) === String(lastServiceId))) {
    return String(lastServiceId);
  }

  return services.length === 1 ? String(services[0].id) : "";
};

/** The offered slot that starts at `time` ("HH:mm"), if any. */
export const matchSlot = (slots, time) =>
  (time && slots.find((slot) => formatSlotLabel(slot) === time)) || "";

const QuickBookingModal = ({
  initial,
  resources,
  services,
  defaultResourceId = "",
  onClose,
  onCreated,
}) => {
  const { t, i18n } = useTranslation();
  const nameRef = useRef(null);

  const [resourceId, setResourceId] = useState(
    defaultResourceId || (resources.length === 1 ? String(resources[0].id) : ""),
  );
  const [serviceId, setServiceId] = useState(() =>
    pickService(
      servicesForResource(services, defaultResourceId),
      initial?.durationMinutes,
      readLastServiceId(),
    ),
  );
  const [bookingDate, setBookingDate] = useState(initial?.date || "");
  const [timeSlot, setTimeSlot] = useState("");

  // The time the calendar was clicked at. Kept apart from `timeSlot` so it can
  // be matched again whenever the slot list is refetched (another service has
  // another grid), and so the form can say "10:15 is not available" instead of
  // silently picking something else. Read through a ref by the fetch, which
  // must not rerun just because this changed.
  const [requestedTime, setRequestedTime] = useState(initial?.time || "");
  const requestedTimeRef = useRef(requestedTime);
  requestedTimeRef.current = requestedTime;

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bumped to refetch the slots after a conflict, when the list on screen is
  // known to be stale.
  const [slotsVersion, setSlotsVersion] = useState(0);

  const availableServices = useMemo(
    () => servicesForResource(services, resourceId),
    [services, resourceId],
  );

  // Services and resources can arrive after the modal opens (the calendar
  // fetches them in the background). Fill the gaps once they do, without
  // overriding anything already chosen.
  useEffect(() => {
    if (!resourceId && resources.length === 1) setResourceId(String(resources[0].id));
  }, [resources, resourceId]);

  useEffect(() => {
    const stillValid = availableServices.some((s) => String(s.id) === serviceId);
    if (serviceId && stillValid) return;
    setServiceId(pickService(availableServices, initial?.durationMinutes, readLastServiceId()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableServices]);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!resourceId || !serviceId || !bookingDate) {
      setSlots([]);
      setTimeSlot("");
      return undefined;
    }

    let cancelled = false;
    setLoadingSlots(true);

    GetAvailableTimeSlotsAuthAction(resourceId, serviceId, bookingDate)
      .then((data) => {
        if (cancelled) return;
        const offered = data || [];
        setSlots(offered);
        // Keep the current choice if it is still on offer; otherwise fall back
        // to the time the calendar was clicked at.
        setTimeSlot((current) =>
          current && offered.includes(current)
            ? current
            : matchSlot(offered, requestedTimeRef.current),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setSlots([]);
        setTimeSlot("");
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resourceId, serviceId, bookingDate, slotsVersion]);

  const requestedUnavailable =
    !loadingSlots &&
    Boolean(requestedTime) &&
    !timeSlot &&
    slots.length > 0 &&
    !matchSlot(slots, requestedTime);

  const selectedService = services.find((s) => String(s.id) === serviceId);

  const headline = bookingDate
    ? fromISODate(bookingDate).toLocaleDateString(i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!customerName.trim() || !timeSlot) return;

    setSaving(true);

    try {
      const created = await CreateBookingAction({
        serviceId: Number(serviceId),
        resourceId: Number(resourceId),
        bookingDate,
        timeSlot,
        customerName: customerName.trim(),
        // Null, never "": the API's email check passes an absent address and
        // rejects an empty one.
        customerEmail: customerEmail.trim() || null,
        customerPhone: customerPhone.trim() || null,
        notes: notes.trim() || null,
      });

      rememberServiceId(serviceId);
      notify.success(t("Dashboard.Bookings.notices.created"));
      onCreated(created);
    } catch (err) {
      // The time went while the form was open: not a fault, pick another one.
      if (err.response?.status === 409) {
        notify.warning(t("Dashboard.Calendar.quick.errors.slotTaken"));
        setTimeSlot("");
        setSlotsVersion((v) => v + 1);
      } else {
        notifySaveError(err, t("Dashboard.Calendar.quick.errors.saveFailed"));
      }
    } finally {
      setSaving(false);
    }
  };

  const slotPlaceholder = loadingSlots
    ? t("Dashboard.Bookings.form.loading")
    : !resourceId || !serviceId || !bookingDate
      ? t("Dashboard.Bookings.form.selectResourceServiceDateFirst")
      : slots.length === 0
        ? t("Dashboard.Calendar.quick.noSlots")
        : t("Dashboard.Bookings.form.selectTime");

  return (
    <div
      className={modalOverlayClass}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-booking-title"
    >
      <form
        className={modalPanelClass}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className={modalHeaderClass}>
          <div className="min-w-0">
            <h2 id="quick-booking-title" className={cardTitleClass}>
              {t("Dashboard.Calendar.quick.title")}
            </h2>
            {headline && (
              <p className="m-0 mt-0.5 truncate text-xs text-ink-500 first-letter:uppercase">
                {headline}
                {timeSlot && ` · ${formatSlotLabel(timeSlot)}`}
                {selectedService && serviceMinutes(selectedService) > 0 &&
                  ` · ${t("Dashboard.Calendar.quick.minutes", { count: serviceMinutes(selectedService) })}`}
              </p>
            )}
          </div>
          <button
            type="button"
            className={modalCloseClass}
            onClick={onClose}
            aria-label={t("Dashboard.Bookings.modal.close")}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <div className={modalBodyClass}>
          <div className={formGridClass}>
            <div className={formFullWidthClass}>
              <label htmlFor="quick-customerName" className={formLabelClass}>
                {t("Dashboard.Calendar.quick.customerName")}{" "}
                <span className={requiredMarkClass}>*</span>
              </label>
              <input
                ref={nameRef}
                id="quick-customerName"
                name="customerName"
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className={formControlClass}
                required
                maxLength={200}
                autoComplete="off"
                placeholder={t("Dashboard.Calendar.quick.customerNamePlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="quick-customerPhone" className={formLabelClass}>
                {t("Dashboard.Calendar.quick.phone")}{" "}
                <span className="font-normal text-ink-400">{t("Dashboard.Calendar.quick.optional")}</span>
              </label>
              <input
                id="quick-customerPhone"
                name="customerPhone"
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className={formControlClass}
                maxLength={20}
                autoComplete="off"
              />
            </div>

            <div>
              <label htmlFor="quick-customerEmail" className={formLabelClass}>
                {t("Dashboard.Calendar.quick.email")}{" "}
                <span className="font-normal text-ink-400">{t("Dashboard.Calendar.quick.optional")}</span>
              </label>
              <input
                id="quick-customerEmail"
                name="customerEmail"
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className={formControlClass}
                maxLength={255}
                autoComplete="off"
              />
            </div>

            <div>
              <label htmlFor="quick-serviceId" className={formLabelClass}>
                {t("Dashboard.Bookings.form.service")} <span className={requiredMarkClass}>*</span>
              </label>
              <select
                id="quick-serviceId"
                name="serviceId"
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
                className={formControlClass}
                required
              >
                <option value="">{t("Dashboard.Bookings.form.selectService")}</option>
                {availableServices.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({t("Dashboard.Calendar.quick.minutes", { count: serviceMinutes(s) })})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="quick-resourceId" className={formLabelClass}>
                {t("Dashboard.Bookings.form.resource")} <span className={requiredMarkClass}>*</span>
              </label>
              <select
                id="quick-resourceId"
                name="resourceId"
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                className={formControlClass}
                required
              >
                <option value="">{t("Dashboard.Bookings.form.selectResource")}</option>
                {resources.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="quick-bookingDate" className={formLabelClass}>
                {t("Dashboard.Bookings.form.date")} <span className={requiredMarkClass}>*</span>
              </label>
              <input
                id="quick-bookingDate"
                name="bookingDate"
                type="date"
                value={bookingDate}
                onChange={(e) => {
                  setBookingDate(e.target.value);
                  setTimeSlot("");
                }}
                className={formControlClass}
                required
              />
            </div>

            <div>
              <label htmlFor="quick-timeSlot" className={formLabelClass}>
                {t("Dashboard.Bookings.form.time")} <span className={requiredMarkClass}>*</span>
              </label>
              <select
                id="quick-timeSlot"
                name="timeSlot"
                value={timeSlot}
                onChange={(e) => {
                  setTimeSlot(e.target.value);
                  // An explicit choice replaces the clicked time.
                  setRequestedTime("");
                }}
                className={formControlClass}
                required
                disabled={loadingSlots || slots.length === 0}
              >
                <option value="">{slotPlaceholder}</option>
                {slots.map((slot) => (
                  <option key={slot} value={slot}>
                    {formatSlotLabel(slot)}
                  </option>
                ))}
              </select>
            </div>

            {requestedUnavailable && (
              <div className={`${alertInfoClass} ${formFullWidthClass}`} role="status">
                <i className="bi bi-info-circle mt-0.5 shrink-0" aria-hidden="true" />
                <span className="flex-1">
                  {t("Dashboard.Calendar.quick.requestedUnavailable", { time: requestedTime })}
                </span>
              </div>
            )}

            <div className={formFullWidthClass}>
              <label htmlFor="quick-notes" className={formLabelClass}>
                {t("Dashboard.Bookings.form.notes")}{" "}
                <span className="font-normal text-ink-400">{t("Dashboard.Calendar.quick.optional")}</span>
              </label>
              <input
                id="quick-notes"
                name="notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={formControlClass}
                maxLength={1000}
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className={modalFooterClass}>
          <button type="button" className={btnSecondaryClass} onClick={onClose} disabled={saving}>
            {t("Dashboard.Calendar.quick.cancel")}
          </button>
          <button
            type="submit"
            className={btnPrimaryClass}
            disabled={saving || !customerName.trim() || !timeSlot}
          >
            <i className="bi bi-check-lg" aria-hidden="true" />
            {saving ? t("Dashboard.Calendar.quick.saving") : t("Dashboard.Calendar.quick.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuickBookingModal;
